import { afterNextRender, ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Injector, Input, OnDestroy, OnInit, Output, signal, ViewChild, WritableSignal } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ToastrService } from 'ngx-toastr';
import { Observable } from 'rxjs';
import { finalize, take } from 'rxjs/operators';
import { INTENT_DOMAIN_SCAN_LOADER_MESSAGES } from 'src/app/shared/constants';
import { AccountIntentTopic, DomainTopicsDto, IntentTopic } from 'src/app/shared/dto';
import { IntentSourceEnum } from 'src/app/shared/enums';
import { domainValidator } from 'src/app/shared/helpers/domain.validator';
import { AccountApiService } from 'src/app/shared/services/api/account-api.service';
import { ConvertedIntentTopicInterface } from '../../models/converted-intent-topic.interface';
import { parse } from 'tldts';
import { FormChangeStateService } from '../../../../components';

export enum IntentRecommendationWizardStep {
    DOMAIN = 'domain',
    TOPICS = 'topics',
}

/** Topic Selection Wizard: domain-scan recommendation flow; edits a working copy applied on finish. */
@UntilDestroy()
@Component({
    selector: 'app-intent-recommendation-wizard',
    templateUrl: './intent-recommendation-wizard.component.html',
    styleUrls: ['./intent-recommendation-wizard.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class IntentRecommendationWizardComponent implements OnInit, OnDestroy {
    @Input() source: IntentSourceEnum.BOMBORA | IntentSourceEnum.SALES_INTEL;
    @Input() plans = [];
    @Input() planPropertyValues;
    /** Full source catalog — used to group the flat scan results by category. */
    @Input() catalogTopics: ConvertedIntentTopicInterface[] = [];
    @Input() set accountTopics(topics: AccountIntentTopic[]) {
        this.workingTopics = topics ? [...topics] : [];
        this.originalAccountTopics = topics ? [...topics] : [];
    }

    @Output() back: EventEmitter<void> = new EventEmitter<void>();
    @Output() finish: EventEmitter<AccountIntentTopic[]> = new EventEmitter<AccountIntentTopic[]>();

    @ViewChild('companyWebsiteInput', { read: ElementRef }) private companyWebsiteInput?: ElementRef<HTMLInputElement>;

    private originalAccountTopics: AccountIntentTopic[] = [];
    private loaderMessageIntervalId: ReturnType<typeof setInterval> | null = null;
    private currentLoaderMessageIndex: number = 0;

    readonly wizardStep: typeof IntentRecommendationWizardStep = IntentRecommendationWizardStep;
    readonly loaderMessages: readonly string[] = INTENT_DOMAIN_SCAN_LOADER_MESSAGES;

    step: WritableSignal<IntentRecommendationWizardStep> = signal(IntentRecommendationWizardStep.DOMAIN);
    domainForm: FormGroup;
    isScanning: WritableSignal<boolean> = signal(false);
    scanFailed: WritableSignal<boolean> = signal(false);
    scanningDomain: string = '';
    domain: string = '';
    generatedTopics: IntentTopic[] = [];
    recommendedGrouped: ConvertedIntentTopicInterface[] = [];
    /** Left-pane list: recommendations plus the account's already-owned topics. */
    displayTopics: ConvertedIntentTopicInterface[] = [];
    workingTopics: AccountIntentTopic[] = [];
    currentLoaderMessage: WritableSignal<string> = signal(this.loaderMessages[0]);

    constructor(
        private readonly formBuilder: FormBuilder,
        private readonly accountApiService: AccountApiService,
        private readonly injector: Injector,
        private readonly toastr: ToastrService,
        private readonly formChangeStateService: FormChangeStateService
    ) {
        this.domainForm = this.formBuilder.group({
            companyWebsite: ['', [Validators.required, domainValidator()]]
        });
    }

    ngOnInit(): void {
        this.syncFinishAvailability();
        // Driven by the modal footer's "Finish" button.
        this.formChangeStateService.wizardFinish()
            .pipe(untilDestroyed(this))
            .subscribe(() => this.onFinish());
    }

    ngOnDestroy(): void {
        this.stopLoaderMessageRotation();
    }

    onContinue(): void {
        if (this.domainForm.invalid || this.isScanning()) {
            return;
        }

        const companyWebsite: string = this.domainForm.value.companyWebsite?.trim();
        this.scanningDomain = parse(companyWebsite)?.domain || companyWebsite;
        this.isScanning.set(true);
        this.scanFailed.set(false);
        this.syncFinishAvailability();
        this.startLoaderMessageRotation();

        const recommendations$: Observable<DomainTopicsDto> = this.source === IntentSourceEnum.BOMBORA
            ? this.accountApiService.getBomboraDomainRecommendations(companyWebsite)
            : this.accountApiService.getIntentDomainRecommendations(companyWebsite);

        recommendations$
            .pipe(
                untilDestroyed(this),
                // Runs after next/error, so the step & failure flags they set are already settled here.
                finalize(() => {
                    this.isScanning.set(false);
                    this.syncFinishAvailability();
                    this.stopLoaderMessageRotation();
                })
            )
            .subscribe({
                next: (response: DomainTopicsDto) => {
                    const topics: IntentTopic[] = response?.topics || [];
                    if (!topics.length) {
                        this.scanFailed.set(true);
                        return;
                    }
                    this.domain = response?.domain || companyWebsite;
                    this.generatedTopics = [...topics];
                    this.recommendedGrouped = this.buildRecommendedGrouped(this.generatedTopics, this.catalogTopics);
                    this.buildDisplayTopics();
                    this.step.set(IntentRecommendationWizardStep.TOPICS);
                },
                error: () => {
                    this.scanFailed.set(true);
                }
            });
    }

    onTryAgain(): void {
        this.scanFailed.set(false);
        this.step.set(IntentRecommendationWizardStep.DOMAIN);
        this.syncFinishAvailability();
        // Focus the input once the domain step has rendered, so the user can edit/replace the domain.
        afterNextRender(
            () => this.companyWebsiteInput?.nativeElement.focus(),
            { injector: this.injector }
        );
    }

    onWorkingTopicsChange(topics: AccountIntentTopic[]): void {
        this.workingTopics = [...topics];
        this.syncWizardChanges();
    }

    /** Add all recommended topics, or warn and add nothing if they don't all fit the plan limit. */
    onAddAllRecommended(): void {
        const maxTopics: number = this.planPropertyValues?.maxTopics ?? Number.MAX_SAFE_INTEGER;
        const selectedIds: Set<number> = new Set(this.workingTopics.map((topic: AccountIntentTopic) => topic.topic_id));
        const toAdd: ConvertedIntentTopicInterface[] = this.recommendedGrouped.filter(
            (topic: ConvertedIntentTopicInterface) => topic.topic_id && !selectedIds.has(topic.topic_id)
        );

        if (this.workingTopics.length + toAdd.length > maxTopics) {
            this.toastr.warning('Maximum of ' + maxTopics + ' topics can be selected.');
            return;
        }

        this.workingTopics = [
            ...this.workingTopics,
            ...toAdd.map((topic: ConvertedIntentTopicInterface) => ({
                topic_id: topic.topic_id,
                topic_name: topic.topic_name,
                intent_source: this.source,
            } as AccountIntentTopic)),
        ];
        this.syncWizardChanges();
    }

    onRemoveAllRecommended(): void {
        this.workingTopics = [];
        this.syncWizardChanges();
    }

    onBack(): void {
        // "‹ Back to main" with pending changes prompts to finalize/discard (same modal as the ✕);
        // proceed → discard and return to main, finalize → apply and return to main.
        if (!this.hasWorkingChanges()) {
            this.back.emit();
            return;
        }
        this.formChangeStateService.showFinalizeWarning()
            .pipe(take(1), untilDestroyed(this))
            .subscribe((action) => {
                if (action === 'proceed') {
                    this.back.emit();
                } else if (action === 'finalize') {
                    this.onFinish();
                }
            });
    }

    onFinish(): void {
        this.finish.emit(this.workingTopics);
    }

    /** Footer "Finish" only applies once recommendations are on screen — same condition the topics step renders under. */
    private syncFinishAvailability(): void {
        this.formChangeStateService.setWizardFinishEnabled(
            !this.isScanning() && !this.scanFailed() && this.step() === IntentRecommendationWizardStep.TOPICS
        );
    }

    /** Working copy differs from the selection the wizard opened with (order-insensitive by topic id). */
    private hasWorkingChanges(): boolean {
        const original: Set<number> = new Set(this.originalAccountTopics.map((topic: AccountIntentTopic) => topic.topic_id));
        const working: Set<number> = new Set(this.workingTopics.map((topic: AccountIntentTopic) => topic.topic_id));
        if (original.size !== working.size) {
            return true;
        }
        for (const id of working) {
            if (!original.has(id)) {
                return true;
            }
        }
        return false;
    }

    private syncWizardChanges(): void {
        this.formChangeStateService.setWizardHasChanges(this.hasWorkingChanges());
    }

    /** Build the left-pane list once per scan: recommendations plus the account's owned topics, grouped by category. */
    private buildDisplayTopics(): void {
        const recommendedIds: Set<number> = new Set(this.generatedTopics.map((topic: IntentTopic) => topic.topic_id));
        const owned: IntentTopic[] = this.originalAccountTopics
            .filter((topic: AccountIntentTopic) => !recommendedIds.has(topic.topic_id))
            .map((topic: AccountIntentTopic) => ({ topic_id: topic.topic_id, topic_name: topic.topic_name }));

        this.displayTopics = this.buildRecommendedGrouped([...this.generatedTopics, ...owned], this.catalogTopics);
    }

    private startLoaderMessageRotation(): void {
        this.stopLoaderMessageRotation();
        this.currentLoaderMessageIndex = 0;
        this.currentLoaderMessage.set(this.loaderMessages[0]);
        this.loaderMessageIntervalId = setInterval(() => {
            this.currentLoaderMessageIndex = (this.currentLoaderMessageIndex + 1) % this.loaderMessages.length;
            this.currentLoaderMessage.set(this.loaderMessages[this.currentLoaderMessageIndex]);
        }, 10000);
    }

    private stopLoaderMessageRotation(): void {
        if (this.loaderMessageIntervalId) {
            clearInterval(this.loaderMessageIntervalId);
            this.loaderMessageIntervalId = null;
        }
        this.currentLoaderMessageIndex = 0;
        this.currentLoaderMessage.set(this.loaderMessages[0]);
    }

    /** Group the flat scan results by category via the catalog; unknown topics are appended ungrouped. */
    private buildRecommendedGrouped(
        recommended: IntentTopic[],
        catalog: ConvertedIntentTopicInterface[]
    ): ConvertedIntentTopicInterface[] {
        const remainingIds: Set<number> = new Set(recommended.map((topic: IntentTopic) => topic.topic_id));
        const grouped: ConvertedIntentTopicInterface[] = [];

        let pendingCategory: string | null = null;
        let pendingTopics: ConvertedIntentTopicInterface[] = [];
        const flush = (): void => {
            if (pendingCategory && pendingTopics.length) {
                grouped.push(
                    { category: pendingCategory, topic_names: pendingTopics.map((topic) => topic.topic_name) },
                    ...pendingTopics
                );
            }
            pendingTopics = [];
        };

        for (const item of catalog || []) {
            if (item.category) {
                flush();
                pendingCategory = item.category;
            } else if (remainingIds.has(item.topic_id)) {
                pendingTopics.push({ topic_id: item.topic_id, topic_name: item.topic_name });
                remainingIds.delete(item.topic_id);
            }
        }
        flush();

        const leftovers: ConvertedIntentTopicInterface[] = recommended
            .filter((topic: IntentTopic) => remainingIds.has(topic.topic_id))
            .map((topic: IntentTopic) => ({ topic_id: topic.topic_id, topic_name: topic.topic_name }));

        return [...grouped, ...leftovers];
    }
}
